import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { describeExportFailure } from '../http-failure';
import { timeout } from 'rxjs';
import { ExportApiService, PdfExportFlags } from '../services/export-api.service';
import { ChartCaptureService, CaptureFailureKind } from '../services/chart-capture.service';
import { ExportPrerenderService } from '../services/export-prerender.service';
import { ProjectService } from '../services/project.service';
import { UserAuthService } from '../UserAuth.service';

/** SciChart capture names embedded in the PDF (must match server EmbedChart keys). */
export const PDF_CHART_NAMES = [
  'curingGraph',
  'zones',
  'heatmap3D',
  // The Overview page: temperature against liner position. Absent from this list
  // until now, so the server's {OVERVIEW_IMAGE} slot was never supplied and that
  // page has never appeared in a cure report. Silent — the PDF is simply one page
  // short, with nothing logged.
  'temperatureOverDistance',
  'temperatureOverTime',
  'healthLossGraph',
];

/**
 * WHICH EXPORT FLAG ASKS FOR WHICH CHART.
 *
 * The flags were only ever sent to the server, which decided what to embed; the
 * client captured all six regardless. That was harmless while a failed capture
 * was silently replaced by a cached image, but it stopped being harmless the
 * moment a failure refuses the whole report: a chart the user had UNTICKED, or
 * one this project legitimately never had, could block an export of the sections
 * they did ask for.
 *
 * So capture is now gated on the same flags. Only a chart the user actually
 * requested can refuse their report.
 *
 * `temperatureOverDistance` — the Overview page — has NO FLAG and is therefore
 * always required. That is deliberate and Joel's explicit choice, not an
 * oversight: giving it a flag is a separate decision with its own conversation.
 */
const CHART_FLAGS: Partial<Record<string, keyof PdfExportFlags>> = {
  curingGraph: 'CuringGraph',
  zones: 'Zones',
  heatmap3D: 'Graph3D',
  temperatureOverTime: 'TemperatureOverTime',
  healthLossGraph: 'HealthGraph',
};

/** What to call each chart when telling a person which one stopped their report. */
const CHART_LABELS: Record<string, string> = {
  curingGraph: 'Curing Graph',
  zones: 'Zones',
  heatmap3D: '3D Graph',
  temperatureOverDistance: 'Temperature over Distance',
  temperatureOverTime: 'Temperature over Time',
  healthLossGraph: 'Health Graph',
};

const chartLabel = (name: string): string => CHART_LABELS[name] ?? name;

/**
 * The charts this export will actually contain.
 *
 * Exported as a pure function so the POLICY can be tested without standing up
 * the component and its five dependencies — the rule about which chart can
 * refuse a report is the part worth pinning, not the plumbing around it.
 */
export function requestedCharts(flags: PdfExportFlags): string[] {
  return PDF_CHART_NAMES.filter((name) => {
    const flag = CHART_FLAGS[name];
    // No flag means always required — currently only the Overview page.
    return flag === undefined || flags[flag];
  });
}

/**
 * Word the refusal so it names what actually happened.
 *
 * 'absent' and the other two are genuinely different situations and must not be
 * collapsed: a chart this project never had is something the user can correct by
 * unticking it, while a chart that would not draw is a fault worth reporting.
 * Calling the first "failed to render" sends an operator hunting a bug that does
 * not exist.
 */
export function describeCaptureRefusal(
  missing: string[],
  reasons: Record<string, CaptureFailureKind>
): { message: string; detail: string } {
  const absent = missing.filter((n) => reasons[n] === 'absent');
  const broken = missing.filter((n) => reasons[n] !== 'absent');

  const parts: string[] = [];
  if (absent.length > 0) {
    parts.push(
      `you requested ${absent.map(chartLabel).join(', ')}, ` +
        `but this project has ${absent.length > 1 ? 'no such charts' : 'no such chart'}`
    );
  }
  if (broken.length > 0) {
    parts.push(`${broken.map(chartLabel).join(', ')} failed to render`);
  }

  const detail =
    (absent.length > 0 ? `Untick ${absent.map(chartLabel).join(', ')} and export again. ` : '') +
    (broken.length > 0
      ? `Open ${broken.map(chartLabel).join(', ')} and let ${broken.length > 1 ? 'them' : 'it'} draw, then export again. `
      : '') +
    `Details: ${missing.map((n) => `${n} (${reasons[n] ?? 'unknown'})`).join(', ')}`;

  return {
    message: `Report could not be generated: ${parts.join('; ')}. No report was produced.`,
    detail,
  };
}

/**
 * Ceiling on the report request. Generous — the server renders every chart into
 * a PDF — but finite, because a pending request with a spinning spinner is not a
 * state the user can act on.
 */
const PDF_REQUEST_TIMEOUT_MS = 180000;

@Component({
  selector: 'app-export-project',
  templateUrl: './export-project.component.html',
  styleUrls: ['./export-project.component.scss'],
})
export class ExportProjectComponent implements OnInit, OnChanges {
  @Input() projectName: string = '';
  @Input() tenantId: string = '';
  @Input() unitCode: number = 0;
  @Input() exportMode: 'pdf' | 'csv' = 'pdf';

  // The `close` output and goBack() went with the Back button: it was the only
  // thing that ever emitted, so the output was dead the moment it was removed.
  // Leaving an unfired @Output behind reads as an extension point that works.

  mode: 'pdf' | 'csv' = 'pdf';
  isGenerating = false;
  errorMessage = '';
  /** The server's own words, shown under the message. See describeExportFailure. */
  errorDetail = '';

  flags: PdfExportFlags = {
    Zones: true,
    UserParameters: true,
    CuringGraph: true,
    Graph3D: true,
    TemperatureOverTime: true,
    Notes: true,
    Log: true,
    HealthGraph: true,
  };

  readonly sections: Array<{ key: keyof PdfExportFlags; label: string }> = [
    { key: 'Zones', label: 'Zones' },
    { key: 'UserParameters', label: 'User Parameters' },
    { key: 'CuringGraph', label: 'Curing Graph' },
    { key: 'Graph3D', label: '3D Graph' },
    { key: 'TemperatureOverTime', label: 'Temperature Over Time' },
    { key: 'HealthGraph', label: 'Health Graph' },
    { key: 'Notes', label: 'Notes' },
    { key: 'Log', label: 'Fiber Fault Log' },
  ];

  constructor(
    private exportApi: ExportApiService,
    private chartCapture: ChartCaptureService,
    private exportPrerender: ExportPrerenderService,
    private projectService: ProjectService,
    private userAuth: UserAuthService,
  ) {}

  ngOnInit(): void {
    this.mode = this.exportMode;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['exportMode']) {
      this.mode = this.exportMode;
      this.errorMessage = '';
      this.errorDetail = '';
    }
  }

  setMode(m: 'pdf' | 'csv'): void {
    this.mode = m;
    this.errorMessage = '';
    this.errorDetail = '';
  }

  generate(): void {
    if (!this.projectName) return;
    this.isGenerating = true;
    this.errorMessage = '';
    this.errorDetail = '';

    const customerId = this.userAuth.getActiveCustomerId();

    if (this.mode === 'csv') {
      this.exportApi
        .generateCSV(this.projectName, this.tenantId, this.unitCode, customerId)
        .subscribe({
          next: (blob) => {
            const filename = `${this.projectName}_${this.formatDate()}.csv`;
            this.exportApi.downloadBlob(blob, filename);
            this.isGenerating = false;
          },
          error: (err) => {
            // The server usually says why. "Please try again" in front of a
            // permanent authorisation denial is an instruction to do the wrong
            // thing repeatedly — the caller retries, escalates, and is eventually
            // told it is a bug somewhere else.
            const f = describeExportFailure('CSV', err);
            this.errorMessage = f.message;
            this.errorDetail = f.detail;
            this.isGenerating = false;
          },
        });
    } else {
      this.generatePdfWithPrerender(customerId);
    }
  }

  /**
   * PDF generation with offscreen render-on-export: chart components whose
   * surfaces were never registered (tab not visited this session) are
   * instantiated offscreen so every chart image reaches the report. The 5s
   * realtime poll is paused meanwhile — concurrent chart components would each
   * append the same RT row into the shared data arrays.
   */
  private async generatePdfWithPrerender(customerId: string | null): Promise<void> {
    let prerendered: Awaited<ReturnType<ExportPrerenderService['prerenderMissing']>> = [];
    this.projectService.suspendRealtime = true;
    try {
      // Only the charts this report will actually contain. A chart whose flag is
      // off is not captured, cannot fail, and therefore cannot refuse the report.
      const requested = requestedCharts(this.flags);

      prerendered = await this.exportPrerender.prerenderMissing(requested);
      const chartImages = await this.chartCapture.captureAllForExport(requested);

      // REFUSE TO BUILD A REPORT THAT IS MISSING A CHART.
      //
      // This used to warn and then generate anyway, so the operator received a
      // PDF plus a message saying parts of it were absent. Worse, the capture
      // layer would quietly substitute the CACHED on-screen snapshot for a chart
      // it could not render, so the report often looked complete and correct
      // while the chart it depicted was broken — stale data presented as current,
      // in the one artefact customers actually read.
      //
      // A wrong report is worse than no report. Both halves of that are now true:
      // ChartCaptureService no longer substitutes, and this refuses to POST when
      // anything is missing. The operator gets a named chart and can retry.
      //
      // NOT THE SAME AS AN EMPTY REPORT, deliberately. A project with nothing to
      // show still generates a PDF, with empty sections. This path produces NO
      // FILE AT ALL and says why — the two outcomes must stay distinguishable,
      // because "the cure had no faults" and "we could not draw the fault chart"
      // are opposite conclusions from the same blank page.
      const missing = this.chartCapture.lastMissing;
      if (missing.length > 0) {
        const refusal = describeCaptureRefusal(missing, this.chartCapture.lastFailures);
        this.errorMessage = refusal.message;
        this.errorDetail = refusal.detail;
        this.isGenerating = false;
        return;
      }

      // A BOUNDED request, always reaching a terminal state.
      //
      // With no chart images the POST could sit pending indefinitely — observed
      // at 144 s with no PDF, no error and the spinner still turning. An export
      // that neither succeeds nor fails is the worst of the three outcomes: the
      // user cannot retry, cannot proceed, and has nothing to report.
      this.exportApi
        .generatePDF(this.projectName, this.tenantId, this.unitCode, this.flags, chartImages, customerId)
        .pipe(timeout(PDF_REQUEST_TIMEOUT_MS))
        .subscribe({
          next: (blob) => {
            const filename = `${this.projectName}_${this.formatDate()}.pdf`;
            this.exportApi.downloadBlob(blob, filename);
            this.isGenerating = false;
          },
          error: (err) => {
            // A timeout IS worth retrying — it is the one case here where the
            // same request can plausibly succeed next time. Everything else goes
            // through describeExportFailure, which reads the server's reason and
            // only says "try again" when trying again could work.
            if (err?.name === 'TimeoutError') {
              this.errorMessage =
                `The report did not come back within ${Math.round(PDF_REQUEST_TIMEOUT_MS / 1000)}s. Please try again.`;
              this.errorDetail = '';
            } else {
              const f = describeExportFailure('PDF', err);
              this.errorMessage = f.message;
              this.errorDetail = f.detail;
            }
            this.isGenerating = false;
          },
        });
    } catch (err) {
      console.warn('Export: capture/prerender failed', err);
      this.errorMessage = 'Failed to capture chart images. Please try again.';
      this.isGenerating = false;
    } finally {
      this.exportPrerender.destroy(prerendered);
      this.projectService.suspendRealtime = false;
    }
  }

  private formatDate(): string {
    const now = new Date();
    return now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  }
}
